// NewsAlertSystem.js — Enhanced: full alert management, Guardian API, preferences, digests
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, Alert, FlatList, Modal,
  Animated, Dimensions, Switch, ActivityIndicator, TextInput,
  RefreshControl, ScrollView, Platform,
} from 'react-native';
import {
  Ionicons, MaterialCommunityIcons, FontAwesome5, MaterialIcons,
} from '@expo/vector-icons';

const { width, height } = Dimensions.get('window');

// ─────────────────────────────────────────────────────────────────────────────
// CONFIG
// ─────────────────────────────────────────────────────────────────────────────
const GUARDIAN_API_KEY = '96d3065f-e31b-47fe-80d5-96a2664509e9';
const GUARDIAN_BASE    = 'https://content.guardianapis.com';
const ALERT_FIELDS     = 'trailText,body,thumbnail,byline,firstPublicationDate';
const AUTO_REFRESH_MS  = 30000; // 30 seconds

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const guardianUrl = (path, extra = '') =>
  `${GUARDIAN_BASE}${path}?show-fields=${ALERT_FIELDS}&api-key=${GUARDIAN_API_KEY}${extra}`;

const mapToAlert = (article) => ({
  id:        article.id,
  newsId:    article.id,
  title:     article.webTitle,
  message:   article.fields?.trailText || 'Tap to read more.',
  type:      article.sectionName === 'Politics' ? 'breaking' : 'update',
  priority:  article.webTitle.toLowerCase().includes('breaking') ? 'high' : 'medium',
  timestamp: new Date(article.webPublicationDate),
  category:  article.sectionName || 'Technology',
  imageUrl:  article.fields?.thumbnail || null,
  read:      false,
  dismissed: false,
  snoozedUntil: null,
  pinned:    false,
  starred:   false,
  apiData:   article,
});

const mapToArticle = (article) => ({
  id:          article.id,
  title:       article.webTitle,
  snippet:     article.fields?.trailText || '',
  content:     article.fields?.body || '<p>No content available.</p>',
  category:    article.sectionName || 'Technology',
  source:      'The Guardian',
  author:      article.fields?.byline || 'The Guardian',
  publishDate: new Date(article.webPublicationDate),
  lastModified:new Date(article.webPublicationDate),
  status:      'published',
  priority:    article.webTitle.toLowerCase().includes('breaking') ? 'breaking' : 'medium',
  tags:        article.tags ? article.tags.map(t => t.webTitle) : [],
  imageUrl:    article.fields?.thumbnail,
  readTime:    article.fields?.body ? Math.ceil(article.fields.body.length / 1500) : 5,
  views:       Math.floor(Math.random() * 10000),
  likes:       Math.floor(Math.random() * 500),
  shares:      Math.floor(Math.random() * 200),
});

const mockAlert = (i = 0) => ({
  id:         `mock-${Date.now()}-${i}`,
  newsId:     `mock-article-${i}`,
  title:      ['Engineering Sector Growth Accelerates', 'AI Engineer Demand Surges', 'Green Energy Jobs Record', 'Salary Increase Beats Inflation', 'UK Tech Hub Expansion'][i % 5],
  message:    'Important developments in the UK engineering sector. Tap to read the full article.',
  type:       i === 0 ? 'breaking' : 'update',
  priority:   i === 0 ? 'high' : i < 2 ? 'medium' : 'low',
  timestamp:  new Date(Date.now() - i * 3600000),
  category:   ['Technology', 'Finance', 'Environment', 'Education', 'Policy'][i % 5],
  imageUrl:   null,
  read:       false,
  dismissed:  false,
  snoozedUntil: null,
  pinned:     false,
  starred:    false,
});

const formatTime = (ts) => {
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (m < 1)  return 'Just now';
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  if (d < 7)  return `${d}d ago`;
  return new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const ALERT_COLORS = {
  breaking: '#dc2626',
  high:     '#ef4444',
  medium:   '#f59e0b',
  low:      '#6b7280',
};
const ALERT_ICONS  = { breaking: 'flash', update: 'information', reminder: 'clock-outline' };
const CATEGORY_COLORS = {
  Technology:  '#0e4e96', Finance: '#10b981', Environment: '#22c55e',
  Education:   '#8b5cf6', Policy: '#f59e0b', Politics: '#dc2626',
};

// ─────────────────────────────────────────────────────────────────────────────
// BADGE (floating bell button)
// ─────────────────────────────────────────────────────────────────────────────
export const NewsAlertBadge = ({ alertCount, onPress }) => {
  const pulseAnim = useRef(new Animated.Value(1)).current;
  const shakeAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (alertCount > 0) {
      const pulse = Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, { toValue: 1.2, duration: 800, useNativeDriver: true }),
          Animated.timing(pulseAnim, { toValue: 1,   duration: 800, useNativeDriver: true }),
        ])
      );
      pulse.start();
      // Shake on new alert
      Animated.sequence([
        Animated.timing(shakeAnim, { toValue: 6, duration: 60, useNativeDriver: true }),
        Animated.timing(shakeAnim, { toValue: -6, duration: 60, useNativeDriver: true }),
        Animated.timing(shakeAnim, { toValue: 4, duration: 60, useNativeDriver: true }),
        Animated.timing(shakeAnim, { toValue: 0, duration: 60, useNativeDriver: true }),
      ]).start();
      return () => pulse.stop();
    }
  }, [alertCount]);

  return (
    <TouchableOpacity style={styles.alertBadgeContainer} onPress={onPress}>
      <Animated.View style={[styles.alertBadge, {
        transform: [{ scale: pulseAnim }, { translateX: shakeAnim }],
      }]}>
        <MaterialCommunityIcons name="bell" size={20} color="#fff" />
        {alertCount > 0 && (
          <View style={styles.alertCount}>
            <Text style={styles.alertCountText}>{alertCount > 99 ? '99+' : alertCount}</Text>
          </View>
        )}
      </Animated.View>
    </TouchableOpacity>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN MODAL
// ─────────────────────────────────────────────────────────────────────────────
export const NewsAlertSystem = ({ visible, onClose, onNavigateToNews }) => {
  const [alerts,          setAlerts]          = useState([]);
  const [loading,         setLoading]         = useState(false);
  const [refreshing,      setRefreshing]      = useState(false);
  const [filterType,      setFilterType]      = useState('all');
  const [sortBy,          setSortBy]          = useState('newest');
  const [searchQuery,     setSearchQuery]     = useState('');
  const [showPrefs,       setShowPrefs]       = useState(false);
  const [showDigest,      setShowDigest]      = useState(false);
  const [showSort,        setShowSort]        = useState(false);
  const [selectedCategory,setSelectedCategory]= useState('all');
  const [keywordAlert,    setKeywordAlert]    = useState('');
  const [keywords,        setKeywords]        = useState(['engineering', 'UK jobs', 'salary']);

  const [prefs, setPrefs] = useState({
    breaking:        true,
    updates:         true,
    reminders:       false,
    sound:           true,
    vibration:       true,
    digest:          false,
    digestFrequency: 'daily',
    minPriority:     'low',
    categories:      { Technology: true, Finance: true, Environment: true, Education: true, Policy: true },
  });

  const slideAnim = useRef(new Animated.Value(height)).current;

  // Animate modal
  useEffect(() => {
    Animated.spring(slideAnim, {
      toValue:  visible ? 0 : height,
      useNativeDriver: true,
      tension:  90,
      friction: 9,
    }).start();
  }, [visible]);

  // Fetch on mount and auto-refresh
  useEffect(() => {
    if (!visible) return;
    loadAlerts();
    const interval = setInterval(loadAlerts, AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, [visible]);

  const loadAlerts = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const res  = await fetch(guardianUrl('/search', '&tag=tone/news&order-by=newest&page-size=20'));
      const json = await res.json();
      const fetched = (json.response?.results || []).map(mapToAlert);
      setAlerts(prev => {
        const existingIds = new Set(prev.map(a => a.id));
        const fresh = fetched.filter(a => !existingIds.has(a.id));
        return [...fresh, ...prev].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      });
    } catch {
      if (alerts.length === 0) {
        setAlerts(Array.from({ length: 8 }, (_, i) => mockAlert(i)));
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = () => { setRefreshing(true); loadAlerts(); };

  // Alert actions
  const markRead    = id   => setAlerts(p => p.map(a => a.id === id ? { ...a, read: true } : a));
  const dismiss     = id   => setAlerts(p => p.map(a => a.id === id ? { ...a, dismissed: true } : a));
  const togglePin   = id   => setAlerts(p => p.map(a => a.id === id ? { ...a, pinned: !a.pinned } : a));
  const toggleStar  = id   => setAlerts(p => p.map(a => a.id === id ? { ...a, starred: !a.starred } : a));
  const snooze      = id   => {
    const until = new Date(Date.now() + 3600000);
    setAlerts(p => p.map(a => a.id === id ? { ...a, snoozedUntil: until } : a));
    Alert.alert('Snoozed', 'Alert snoozed for 1 hour.');
  };

  const markAllRead  = () => setAlerts(p => p.map(a => ({ ...a, read: true })));
  const clearAll     = () => {
    Alert.alert('Clear All', 'Dismiss all alerts?', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Clear', style: 'destructive', onPress: () => setAlerts(p => p.map(a => ({ ...a, dismissed: true }))) },
    ]);
  };

  const addKeyword = () => {
    if (!keywordAlert.trim()) return;
    setKeywords(p => [...new Set([...p, keywordAlert.trim()])]);
    setKeywordAlert('');
    Alert.alert('Keyword Added', `You'll be alerted when "${keywordAlert}" is mentioned.`);
  };

  // Navigate to article
  const handleAlertPress = async (alert) => {
    markRead(alert.id);
    setLoading(true);
    try {
      const res  = await fetch(guardianUrl(`/${alert.newsId}`));
      const json = await res.json();
      const art  = json.response?.content ? mapToArticle(json.response.content) : null;
      onNavigateToNews?.(art || generateFallbackArticle(alert));
    } catch {
      onNavigateToNews?.(generateFallbackArticle(alert));
    } finally {
      setLoading(false);
      onClose();
    }
  };

  const generateFallbackArticle = (alert) => ({
    id: alert.id, title: alert.title, snippet: alert.message,
    content: `<p>${alert.message}</p>`, category: alert.category,
    source: 'The Guardian', author: 'News Desk', publishDate: new Date(alert.timestamp),
    priority: alert.priority, readTime: 3, views: 0, likes: 0, shares: 0,
  });

  // Filtered + sorted alerts
  const now = Date.now();
  const filtered = alerts.filter(a => {
    if (a.dismissed) return false;
    if (a.snoozedUntil && new Date(a.snoozedUntil).getTime() > now) return false;
    if (filterType === 'unread'   && a.read)                return false;
    if (filterType === 'breaking' && a.type !== 'breaking') return false;
    if (filterType === 'pinned'   && !a.pinned)             return false;
    if (filterType === 'starred'  && !a.starred)            return false;
    if (selectedCategory !== 'all' && a.category !== selectedCategory) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return a.title.toLowerCase().includes(q) || a.message.toLowerCase().includes(q);
    }
    return true;
  }).sort((a, b) => {
    if (sortBy === 'newest')   return new Date(b.timestamp) - new Date(a.timestamp);
    if (sortBy === 'oldest')   return new Date(a.timestamp) - new Date(b.timestamp);
    if (sortBy === 'priority') {
      const order = { high: 0, medium: 1, low: 2 };
      return (order[a.priority] || 2) - (order[b.priority] || 2);
    }
    if (sortBy === 'unread')   return a.read - b.read;
    return 0;
  });

  const activeAlerts  = alerts.filter(a => !a.dismissed);
  const unreadCount   = activeAlerts.filter(a => !a.read).length;
  const breakingCount = activeAlerts.filter(a => a.type === 'breaking').length;
  const pinnedCount   = activeAlerts.filter(a => a.pinned).length;
  const categories    = ['all', ...new Set(activeAlerts.map(a => a.category).filter(Boolean))];

  const renderAlert = ({ item }) => {
    const alertColor = ALERT_COLORS[item.type === 'breaking' ? 'breaking' : item.priority] || '#6b7280';
    const catColor   = CATEGORY_COLORS[item.category] || '#6b7280';
    return (
      <TouchableOpacity
        style={[styles.alertItem, !item.read && styles.unreadAlert, item.pinned && styles.pinnedAlert]}
        onPress={() => handleAlertPress(item)}
        activeOpacity={0.75}
        disabled={loading}
      >
        {/* Left accent bar */}
        <View style={[styles.alertAccent, { backgroundColor: alertColor }]} />

        <View style={styles.alertBody}>
          {/* Header row */}
          <View style={styles.alertHeaderRow}>
            <View style={[styles.alertIconWrap, { backgroundColor: alertColor + '22' }]}>
              <MaterialCommunityIcons name={ALERT_ICONS[item.type] || 'bell-outline'} size={16} color={alertColor} />
            </View>
            <View style={styles.alertMeta}>
              <View style={styles.alertBadgeRow}>
                {item.type === 'breaking' && (
                  <View style={styles.breakingBadge}><Text style={styles.breakingText}>BREAKING</Text></View>
                )}
                {item.pinned && <MaterialCommunityIcons name="pin" size={12} color="#0e4e96" />}
                {item.starred && <MaterialCommunityIcons name="star" size={12} color="#fdb913" />}
                <View style={[styles.catBadge, { backgroundColor: catColor + '22' }]}>
                  <Text style={[styles.catBadgeText, { color: catColor }]}>{item.category}</Text>
                </View>
                {!item.read && <View style={styles.unreadDot} />}
              </View>
              <Text style={styles.alertTime}>{formatTime(item.timestamp)}</Text>
            </View>
            {/* Dismiss */}
            <TouchableOpacity onPress={() => dismiss(item.id)} style={styles.dismissBtn}>
              <MaterialIcons name="close" size={16} color="#9ca3af" />
            </TouchableOpacity>
          </View>

          {/* Title + message */}
          <Text style={[styles.alertTitle, !item.read && styles.alertTitleUnread]} numberOfLines={2}>
            {item.title}
          </Text>
          <Text style={styles.alertMessage} numberOfLines={3}>{item.message}</Text>

          {/* Action row */}
          <View style={styles.alertActions}>
            <TouchableOpacity style={styles.alertActionChip} onPress={() => togglePin(item.id)}>
              <MaterialCommunityIcons name={item.pinned ? 'pin-off' : 'pin'} size={13} color="#0e4e96" />
              <Text style={styles.alertActionChipText}>{item.pinned ? 'Unpin' : 'Pin'}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.alertActionChip} onPress={() => toggleStar(item.id)}>
              <MaterialCommunityIcons name={item.starred ? 'star' : 'star-outline'} size={13} color="#f59e0b" />
              <Text style={styles.alertActionChipText}>{item.starred ? 'Starred' : 'Star'}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.alertActionChip} onPress={() => snooze(item.id)}>
              <MaterialCommunityIcons name="alarm-snooze" size={13} color="#6b7280" />
              <Text style={styles.alertActionChipText}>Snooze</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.alertActionChip, styles.alertReadChip]} onPress={() => handleAlertPress(item)}>
              <Text style={styles.alertReadText}>Read →</Text>
            </TouchableOpacity>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  const renderPreferences = () => (
    <ScrollView style={styles.prefsContainer}>
      <View style={styles.prefsHeader}>
        <Text style={styles.prefsTitle}>Alert Preferences</Text>
        <TouchableOpacity onPress={() => setShowPrefs(false)}>
          <MaterialIcons name="close" size={24} color="#6b7280" />
        </TouchableOpacity>
      </View>

      {/* Toggle prefs */}
      {[
        { key: 'breaking',  label: 'Breaking News',     desc: 'Immediate alerts for urgent news' },
        { key: 'updates',   label: 'Industry Updates',  desc: 'Regular sector updates' },
        { key: 'reminders', label: 'Saved Article Reminders', desc: 'Reminders for bookmarked articles' },
        { key: 'sound',     label: 'Sound',             desc: 'Play sound for new alerts' },
        { key: 'vibration', label: 'Vibration',         desc: 'Vibrate on new alerts' },
        { key: 'digest',    label: 'Daily Digest',      desc: 'Get a morning summary of top alerts' },
      ].map(p => (
        <View key={p.key} style={styles.prefItem}>
          <View style={{ flex: 1 }}>
            <Text style={styles.prefLabel}>{p.label}</Text>
            <Text style={styles.prefDesc}>{p.desc}</Text>
          </View>
          <Switch
            value={prefs[p.key]}
            onValueChange={v => setPrefs(prev => ({ ...prev, [p.key]: v }))}
            trackColor={{ false: '#e5e7eb', true: '#0e4e96' }}
            thumbColor={prefs[p.key] ? '#fff' : '#f4f3f4'}
          />
        </View>
      ))}

      {/* Digest frequency */}
      {prefs.digest && (
        <View style={styles.digestFreqRow}>
          <Text style={styles.prefLabel}>Digest Frequency</Text>
          <View style={styles.freqBtns}>
            {['daily', 'weekly'].map(f => (
              <TouchableOpacity key={f} style={[styles.freqBtn, prefs.digestFrequency === f && styles.freqBtnActive]}
                onPress={() => setPrefs(prev => ({ ...prev, digestFrequency: f }))}>
                <Text style={[styles.freqBtnText, prefs.digestFrequency === f && styles.freqBtnTextActive]}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      )}

      {/* Min priority */}
      <View style={styles.prefItem}>
        <Text style={styles.prefLabel}>Minimum Priority</Text>
        <View style={styles.freqBtns}>
          {['high', 'medium', 'low'].map(p => (
            <TouchableOpacity key={p} style={[styles.freqBtn, prefs.minPriority === p && styles.freqBtnActive]}
              onPress={() => setPrefs(prev => ({ ...prev, minPriority: p }))}>
              <Text style={[styles.freqBtnText, prefs.minPriority === p && styles.freqBtnTextActive]}>
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Category toggles */}
      <Text style={[styles.prefLabel, { paddingHorizontal: 20, paddingTop: 16 }]}>Alert Categories</Text>
      {Object.keys(prefs.categories).map(cat => (
        <View key={cat} style={styles.prefItem}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <View style={[styles.catDot, { backgroundColor: CATEGORY_COLORS[cat] || '#6b7280' }]} />
            <Text style={styles.prefLabel}>{cat}</Text>
          </View>
          <Switch
            value={prefs.categories[cat]}
            onValueChange={v => setPrefs(prev => ({ ...prev, categories: { ...prev.categories, [cat]: v } }))}
            trackColor={{ false: '#e5e7eb', true: '#0e4e96' }}
            thumbColor={prefs.categories[cat] ? '#fff' : '#f4f3f4'}
          />
        </View>
      ))}

      {/* Keyword alerts */}
      <Text style={[styles.prefLabel, { paddingHorizontal: 20, paddingTop: 16 }]}>Keyword Alerts</Text>
      <View style={styles.keywordInput}>
        <TextInput
          style={styles.keywordField}
          placeholder="Add keyword (e.g. 'HS2', 'AI engineer')"
          value={keywordAlert}
          onChangeText={setKeywordAlert}
          onSubmitEditing={addKeyword}
        />
        <TouchableOpacity style={styles.keywordAddBtn} onPress={addKeyword}>
          <MaterialIcons name="add" size={20} color="#fff" />
        </TouchableOpacity>
      </View>
      <View style={styles.keywordList}>
        {keywords.map(k => (
          <View key={k} style={styles.keywordChip}>
            <Text style={styles.keywordChipText}>{k}</Text>
            <TouchableOpacity onPress={() => setKeywords(p => p.filter(x => x !== k))}>
              <MaterialIcons name="close" size={12} color="#0e4e96" />
            </TouchableOpacity>
          </View>
        ))}
      </View>

      <TouchableOpacity style={styles.resetBtn} onPress={() => Alert.alert('Reset', 'Reset preferences to defaults?', [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Reset', style: 'destructive', onPress: () => setPrefs({ breaking: true, updates: true, reminders: false, sound: true, vibration: true, digest: false, digestFrequency: 'daily', minPriority: 'low', categories: { Technology: true, Finance: true, Environment: true, Education: true, Policy: true } }) },
      ])}>
        <Text style={styles.resetBtnText}>Reset to Defaults</Text>
      </TouchableOpacity>
    </ScrollView>
  );

  return (
    <Modal visible={visible} transparent animationType="none" onRequestClose={onClose}>
      <View style={styles.modalOverlay}>
        <Animated.View style={[styles.modalContent, { transform: [{ translateY: slideAnim }] }]}>
          {showPrefs ? renderPreferences() : (
            <>
              {/* Header */}
              <View style={styles.modalHeader}>
                <View style={styles.headerLeft}>
                  <View style={styles.headerIconWrap}>
                    <MaterialCommunityIcons name="bell" size={20} color="#0e4e96" />
                  </View>
                  <View>
                    <Text style={styles.modalTitle}>News Alerts {unreadCount > 0 ? `(${unreadCount} new)` : ''}</Text>
                    <Text style={styles.modalSubtitle}>Live from The Guardian · Auto-refreshes every 30s</Text>
                  </View>
                </View>
                <View style={styles.headerRight}>
                  <TouchableOpacity style={styles.headerBtn} onPress={() => setShowSort(true)}>
                    <MaterialCommunityIcons name="sort" size={20} color="#0e4e96" />
                  </TouchableOpacity>
                  <TouchableOpacity style={styles.headerBtn} onPress={() => setShowPrefs(true)}>
                    <MaterialCommunityIcons name="cog" size={20} color="#0e4e96" />
                  </TouchableOpacity>
                  <TouchableOpacity style={styles.headerBtn} onPress={onClose}>
                    <MaterialIcons name="close" size={22} color="#6b7280" />
                  </TouchableOpacity>
                </View>
              </View>

              {/* Search */}
              <View style={styles.searchBar}>
                <Ionicons name="search" size={16} color="#9ca3af" />
                <TextInput
                  style={styles.searchInput}
                  placeholder="Search alerts..."
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                  placeholderTextColor="#9ca3af"
                />
                {searchQuery ? (
                  <TouchableOpacity onPress={() => setSearchQuery('')}>
                    <Ionicons name="close-circle" size={16} color="#9ca3af" />
                  </TouchableOpacity>
                ) : null}
              </View>

              {/* Filter tabs */}
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.filterScroll} contentContainerStyle={styles.filterContainer}>
                {[
                  { id: 'all',      label: `All (${activeAlerts.length})` },
                  { id: 'unread',   label: `Unread (${unreadCount})` },
                  { id: 'breaking', label: `Breaking (${breakingCount})` },
                  { id: 'pinned',   label: `Pinned (${pinnedCount})` },
                  { id: 'starred',  label: 'Starred' },
                ].map(f => (
                  <TouchableOpacity key={f.id} style={[styles.filterTab, filterType === f.id && styles.filterTabActive]}
                    onPress={() => setFilterType(f.id)}>
                    <Text style={[styles.filterTabText, filterType === f.id && styles.filterTabTextActive]}>{f.label}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>

              {/* Category filters */}
              {categories.length > 2 && (
                <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.catScroll} contentContainerStyle={{ paddingHorizontal: 12 }}>
                  {categories.map(cat => (
                    <TouchableOpacity key={cat} style={[styles.catTab, selectedCategory === cat && styles.catTabActive]}
                      onPress={() => setSelectedCategory(cat)}>
                      {cat !== 'all' && <View style={[styles.catDot, { backgroundColor: CATEGORY_COLORS[cat] || '#6b7280', marginRight: 4 }]} />}
                      <Text style={[styles.catTabText, selectedCategory === cat && styles.catTabTextActive]}>
                        {cat === 'all' ? 'All Categories' : cat}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              )}

              {/* Actions bar */}
              <View style={styles.actionsBar}>
                <TouchableOpacity style={styles.actionBtn} onPress={markAllRead}>
                  <MaterialCommunityIcons name="check-all" size={15} color="#0e4e96" />
                  <Text style={styles.actionBtnText}>Mark all read</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.actionBtn} onPress={onRefresh} disabled={loading || refreshing}>
                  <MaterialCommunityIcons name="refresh" size={15} color="#0e4e96" />
                  <Text style={styles.actionBtnText}>Refresh</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[styles.actionBtn, styles.clearBtn]} onPress={clearAll}>
                  <MaterialCommunityIcons name="delete-sweep" size={15} color="#ef4444" />
                  <Text style={[styles.actionBtnText, { color: '#ef4444' }]}>Clear all</Text>
                </TouchableOpacity>
              </View>

              {/* Count */}
              <View style={styles.countRow}>
                <Text style={styles.countText}>{filtered.length} alert{filtered.length !== 1 ? 's' : ''}</Text>
                {loading && <ActivityIndicator size={12} color="#0e4e96" style={{ marginLeft: 8 }} />}
              </View>

              {/* List */}
              {loading && alerts.length === 0 ? (
                <View style={styles.loadingBox}>
                  <ActivityIndicator size={36} color="#0e4e96" />
                  <Text style={styles.loadingText}>Fetching live alerts...</Text>
                </View>
              ) : filtered.length === 0 ? (
                <View style={styles.emptyBox}>
                  <MaterialCommunityIcons name="bell-sleep-outline" size={56} color="#d1d5db" />
                  <Text style={styles.emptyTitle}>{searchQuery ? 'No results' : 'All caught up!'}</Text>
                  <Text style={styles.emptyText}>
                    {searchQuery ? `No alerts matching "${searchQuery}"` : 'No alerts for the current filters'}
                  </Text>
                  <TouchableOpacity style={styles.refreshBtn} onPress={onRefresh}>
                    <MaterialCommunityIcons name="refresh" size={16} color="#0e4e96" />
                    <Text style={styles.refreshBtnText}>Refresh Alerts</Text>
                  </TouchableOpacity>
                </View>
              ) : (
                <FlatList
                  data={filtered}
                  renderItem={renderAlert}
                  keyExtractor={i => i.id}
                  style={styles.list}
                  refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#0e4e96" />}
                  ListFooterComponent={() => (
                    <View style={styles.listFooter}>
                      <Text style={styles.listFooterText}>Auto-refreshes every 30 seconds  ·  Source: The Guardian</Text>
                    </View>
                  )}
                />
              )}
            </>
          )}
        </Animated.View>
      </View>

      {/* Sort modal */}
      <Modal visible={showSort} transparent animationType="slide">
        <TouchableOpacity style={styles.sortOverlay} activeOpacity={1} onPress={() => setShowSort(false)}>
          <View style={styles.sortSheet}>
            <Text style={styles.sortTitle}>Sort Alerts</Text>
            {[
              { id: 'newest',   label: 'Newest First' },
              { id: 'oldest',   label: 'Oldest First' },
              { id: 'priority', label: 'By Priority' },
              { id: 'unread',   label: 'Unread First' },
            ].map(s => (
              <TouchableOpacity key={s.id} style={styles.sortOption} onPress={() => { setSortBy(s.id); setShowSort(false); }}>
                <Text style={[styles.sortOptionText, sortBy === s.id && styles.sortOptionActive]}>{s.label}</Text>
                {sortBy === s.id && <MaterialCommunityIcons name="check" size={18} color="#0e4e96" />}
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      </Modal>
    </Modal>
  );
};

const styles = StyleSheet.create({
  // Badge
  alertBadgeContainer: { padding: 4 },
  alertBadge:          { width: 40, height: 40, borderRadius: 20, backgroundColor: '#0e4e96', justifyContent: 'center', alignItems: 'center' },
  alertCount:          { position: 'absolute', top: -4, right: -4, backgroundColor: '#dc2626', minWidth: 18, height: 18, borderRadius: 9, justifyContent: 'center', alignItems: 'center', paddingHorizontal: 4 },
  alertCountText:      { color: '#fff', fontSize: 10, fontWeight: 'bold' },
  // Modal
  modalOverlay:        { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  modalContent:        { backgroundColor: '#fff', borderTopLeftRadius: 24, borderTopRightRadius: 24, maxHeight: height * 0.92, flex: 1 },
  modalHeader:         { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 18, borderBottomWidth: 1, borderBottomColor: '#f3f4f6' },
  headerLeft:          { flexDirection: 'row', alignItems: 'center', flex: 1 },
  headerIconWrap:      { width: 36, height: 36, borderRadius: 10, backgroundColor: '#e8f0fe', justifyContent: 'center', alignItems: 'center', marginRight: 10 },
  modalTitle:          { fontSize: 16, fontWeight: '700', color: '#1f2937' },
  modalSubtitle:       { fontSize: 11, color: '#9ca3af', marginTop: 1 },
  headerRight:         { flexDirection: 'row' },
  headerBtn:           { padding: 6, marginLeft: 4 },
  // Search
  searchBar:           { flexDirection: 'row', alignItems: 'center', margin: 12, backgroundColor: '#f9fafb', borderRadius: 10, paddingHorizontal: 12, height: 40, borderWidth: 1, borderColor: '#e5e7eb' },
  searchInput:         { flex: 1, marginLeft: 8, fontSize: 14, color: '#374151' },
  // Filter tabs
  filterScroll:        { maxHeight: 44 },
  filterContainer:     { paddingHorizontal: 12, alignItems: 'center' },
  filterTab:           { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 20, marginRight: 8, backgroundColor: '#f3f4f6' },
  filterTabActive:     { backgroundColor: '#0e4e96' },
  filterTabText:       { fontSize: 12, fontWeight: '600', color: '#6b7280' },
  filterTabTextActive: { color: '#fff' },
  // Category tabs
  catScroll:           { maxHeight: 38 },
  catTab:              { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, marginRight: 6, backgroundColor: '#f3f4f6' },
  catTabActive:        { backgroundColor: '#e8f0fe' },
  catTabText:          { fontSize: 11, color: '#6b7280', fontWeight: '500' },
  catTabTextActive:    { color: '#0e4e96', fontWeight: '700' },
  catDot:              { width: 8, height: 8, borderRadius: 4 },
  // Actions
  actionsBar:          { flexDirection: 'row', paddingHorizontal: 12, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#f3f4f6', gap: 8 },
  actionBtn:           { flexDirection: 'row', alignItems: 'center', backgroundColor: '#f0f4ff', paddingHorizontal: 12, paddingVertical: 7, borderRadius: 8, gap: 4 },
  clearBtn:            { backgroundColor: '#fff5f5' },
  actionBtnText:       { fontSize: 12, fontWeight: '600', color: '#0e4e96' },
  countRow:            { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 8 },
  countText:           { fontSize: 12, color: '#9ca3af', fontWeight: '500' },
  // Alert item
  alertItem:           { flexDirection: 'row', backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#f3f4f6' },
  unreadAlert:         { backgroundColor: '#f0f9ff' },
  pinnedAlert:         { backgroundColor: '#fffbeb' },
  alertAccent:         { width: 4, borderRadius: 2, margin: 12, marginRight: 0 },
  alertBody:           { flex: 1, padding: 12 },
  alertHeaderRow:      { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 6 },
  alertIconWrap:       { width: 28, height: 28, borderRadius: 8, justifyContent: 'center', alignItems: 'center', marginRight: 8 },
  alertMeta:           { flex: 1 },
  alertBadgeRow:       { flexDirection: 'row', alignItems: 'center', gap: 4, marginBottom: 2 },
  breakingBadge:       { backgroundColor: '#fecaca', paddingHorizontal: 5, paddingVertical: 1, borderRadius: 4 },
  breakingText:        { fontSize: 9, color: '#dc2626', fontWeight: '900' },
  catBadge:            { paddingHorizontal: 6, paddingVertical: 1, borderRadius: 8 },
  catBadgeText:        { fontSize: 10, fontWeight: '600' },
  unreadDot:           { width: 7, height: 7, borderRadius: 4, backgroundColor: '#0e4e96' },
  alertTime:           { fontSize: 11, color: '#9ca3af' },
  dismissBtn:          { padding: 4 },
  alertTitle:          { fontSize: 14, color: '#374151', lineHeight: 20, marginBottom: 4 },
  alertTitleUnread:    { fontWeight: '700', color: '#1f2937' },
  alertMessage:        { fontSize: 12, color: '#6b7280', lineHeight: 18, marginBottom: 8 },
  alertActions:        { flexDirection: 'row', gap: 6, flexWrap: 'wrap' },
  alertActionChip:     { flexDirection: 'row', alignItems: 'center', backgroundColor: '#f3f4f6', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 8, gap: 3 },
  alertActionChipText: { fontSize: 11, color: '#374151', fontWeight: '500' },
  alertReadChip:       { backgroundColor: '#0e4e96', marginLeft: 'auto' },
  alertReadText:       { fontSize: 11, color: '#fff', fontWeight: '700' },
  // Loading / empty
  list:                { flex: 1 },
  loadingBox:          { justifyContent: 'center', alignItems: 'center', paddingVertical: 60 },
  loadingText:         { marginTop: 14, color: '#6b7280', fontSize: 14 },
  emptyBox:            { justifyContent: 'center', alignItems: 'center', paddingVertical: 50, paddingHorizontal: 30 },
  emptyTitle:          { fontSize: 17, fontWeight: '700', color: '#374151', marginTop: 16, marginBottom: 6 },
  emptyText:           { fontSize: 13, color: '#9ca3af', textAlign: 'center', lineHeight: 19, marginBottom: 20 },
  refreshBtn:          { flexDirection: 'row', alignItems: 'center', backgroundColor: '#f0f4ff', paddingHorizontal: 16, paddingVertical: 10, borderRadius: 10, borderWidth: 1, borderColor: '#0e4e96', gap: 6 },
  refreshBtnText:      { color: '#0e4e96', fontWeight: '600', fontSize: 14 },
  listFooter:          { padding: 20, alignItems: 'center' },
  listFooterText:      { fontSize: 11, color: '#9ca3af', textAlign: 'center' },
  // Preferences
  prefsContainer:      { flex: 1 },
  prefsHeader:         { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 20, borderBottomWidth: 1, borderBottomColor: '#e5e7eb' },
  prefsTitle:          { fontSize: 18, fontWeight: '700', color: '#1f2937' },
  prefItem:            { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 20, paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: '#f3f4f6' },
  prefLabel:           { fontSize: 15, fontWeight: '600', color: '#1f2937', marginBottom: 2 },
  prefDesc:            { fontSize: 12, color: '#9ca3af' },
  digestFreqRow:       { paddingHorizontal: 20, paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: '#f3f4f6' },
  freqBtns:            { flexDirection: 'row', gap: 8, marginTop: 8 },
  freqBtn:             { paddingHorizontal: 14, paddingVertical: 7, borderRadius: 8, backgroundColor: '#f3f4f6' },
  freqBtnActive:       { backgroundColor: '#0e4e96' },
  freqBtnText:         { fontSize: 13, color: '#6b7280', fontWeight: '500' },
  freqBtnTextActive:   { color: '#fff', fontWeight: '700' },
  keywordInput:        { flexDirection: 'row', margin: 16, gap: 8 },
  keywordField:        { flex: 1, backgroundColor: '#f9fafb', borderRadius: 10, paddingHorizontal: 12, height: 40, borderWidth: 1, borderColor: '#e5e7eb', fontSize: 14 },
  keywordAddBtn:       { width: 40, height: 40, borderRadius: 10, backgroundColor: '#0e4e96', justifyContent: 'center', alignItems: 'center' },
  keywordList:         { flexDirection: 'row', flexWrap: 'wrap', paddingHorizontal: 16, gap: 8, marginBottom: 16 },
  keywordChip:         { flexDirection: 'row', alignItems: 'center', backgroundColor: '#e8f0fe', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 14, gap: 5 },
  keywordChipText:     { fontSize: 12, color: '#0e4e96', fontWeight: '600' },
  resetBtn:            { margin: 20, backgroundColor: '#f3f4f6', borderRadius: 10, paddingVertical: 14, alignItems: 'center' },
  resetBtnText:        { fontSize: 14, color: '#6b7280', fontWeight: '600' },
  // Sort modal
  sortOverlay:         { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  sortSheet:           { backgroundColor: '#fff', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 24 },
  sortTitle:           { fontSize: 17, fontWeight: '700', color: '#1f2937', marginBottom: 16 },
  sortOption:          { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: '#f3f4f6' },
  sortOptionText:      { fontSize: 15, color: '#374151' },
  sortOptionActive:    { color: '#0e4e96', fontWeight: '700' },
});
